import React from 'react';
import ChatInitModal from '../chat-init-modal/index';
import './chatbot.css';
import { t, getCurrentLanguage } from '../../../utils/translations';
import useLanguageStore from '../../../store/languageStore';

class Chatbot2510 extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      scriptLoaded: false,
      chatbotReady: false,
      error: null,
      showSpeechBubble: false,
      showInitModal: false,
      userEmail: null,
      chatInitialized: false,
      currentLanguage: getCurrentLanguage(),
      frontendVersion: '1.0.1' // Increment this number with each deployment
    };
    this.scriptRef = React.createRef();
    this.speechBubbleTimer = null;
    this.linkObserver = null;
    this.periodicLinkCheck = null;
  }

  componentDidMount() {
    this.loadScript();
    this.setupEventListener();
    this.setupChatAnchorClickListener();
    this.setupLinkObserver();
    
    // Show speech bubble permanently
    this.setState({ showSpeechBubble: true });
    
    // Subscribe to language changes
    this.unsubscribe = useLanguageStore.subscribe((state) => {
      this.setState({ currentLanguage: state.currentLanguage });
    });
  }

  componentWillUnmount() {
    // Clean up event listener
    window.removeEventListener('dta-chat-ready', this.handleDtaChatReady);
    
    // Clean up chat anchor click listener
    this.removeChatAnchorClickListener();
    
    // Clean up link observer
    this.disconnectLinkObserver();
    
    // Clear speech bubble timer
    if (this.speechBubbleTimer) {
      clearTimeout(this.speechBubbleTimer);
    }
    
    // Clean up language subscription
    if (this.unsubscribe) {
      this.unsubscribe();
    }
    
    // Remove script if it was added by this component
    if (this.scriptRef.current && this.scriptRef.current.parentNode) {
      this.scriptRef.current.parentNode.removeChild(this.scriptRef.current);
    }
  }

  loadScript = () => {
    // Check if script already exists
    const existingScript = document.querySelector('script[src="https://assets.dta.totvs.ai/client/stable/dta_chat.js"]');
    
    if (existingScript) {
      this.setState({ scriptLoaded: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://assets.dta.totvs.ai/client/stable/dta_chat.js';
    script.defer = true;
    script.onload = () => {
      this.setState({ scriptLoaded: true });
    };
    script.onerror = () => {
      console.error('❌ Failed to load DTA script');
      this.setState({ error: 'Failed to load chatbot script' });
    };

    this.scriptRef.current = script;
    document.body.appendChild(script);
  };

  setupEventListener = () => {
    window.addEventListener('dta-chat-ready', this.handleDtaChatReady, { once: true });
  };

  setupChatAnchorClickListener = () => {
    // Use a small delay to ensure the dta-chat-anchor element is rendered
    setTimeout(() => {
      const chatAnchor = document.querySelector('dta-chat-anchor');
      if (chatAnchor) {
        // Add a click event listener that intercepts the click
        chatAnchor.addEventListener('click', this.handleChatAnchorClick, true);
        
        // Also try to prevent the default DTA chat behavior until user is verified
        this.preventDefaultHandler = (event) => {
          if (!this.state.chatInitialized) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return false;
          }
        };
        
        chatAnchor.addEventListener('click', this.preventDefaultHandler, true);
        
      } else {
        // Retry after a longer delay if element not found
        setTimeout(() => {
          const retryAnchor = document.querySelector('dta-chat-anchor');
          if (retryAnchor) {
            retryAnchor.addEventListener('click', this.handleChatAnchorClick, true);
            
            this.preventDefaultHandler = (event) => {
              if (!this.state.chatInitialized) {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
                return false;
              }
            };
            
            retryAnchor.addEventListener('click', this.preventDefaultHandler, true);
          }
        }, 500);
      }
    }, 100);
  };

  removeChatAnchorClickListener = () => {
    const chatAnchor = document.querySelector('dta-chat-anchor');
    if (chatAnchor) {
      chatAnchor.removeEventListener('click', this.handleChatAnchorClick, true);
      if (this.preventDefaultHandler) {
        chatAnchor.removeEventListener('click', this.preventDefaultHandler, true);
      }
    }
  };

  handleChatAnchorClick = () => {
    // Track the chat anchor click event for GTM
    this.trackChatAnchorClick();
    
    // Speech bubble remains visible (close functionality commented out)
    // this.closeSpeechBubble();
    
    // If chat is not initialized yet, show the initialization modal
    if (!this.state.chatInitialized) {
      this.setState({ showInitModal: true }, () => {
        // Track modal opening event
        this.trackChatModalOpened();
      });
    }
  };

  trackChatAnchorClick = () => {
    // Send tracking event to GTM dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'chat_anchor_click',
        event_category: 'Chat Interaction',
        event_action: 'Chat Anchor Click',
        event_label: 'DTA Chat Button',
        chat_initialized: this.state.chatInitialized,
        current_language: window.current_language || 'pt',
        element_type: 'dta-chat-anchor',
        component_context: 'chatbot',
        frontend_version: this.state.frontendVersion
      });
    }
  };

  trackChatModalOpened = () => {
    // Send modal opened event to GTM dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'chat_modal_opened',
        event_category: 'Chat Interaction',
        event_action: 'Modal Opened',
        event_label: 'Chat Initialization Modal',
        current_language: window.current_language || 'pt',
        modal_type: 'chat_init_modal',
        component_context: 'chatbot'
      });
    }
  };

  handleCloseInitModal = () => {
    this.setState({ showInitModal: false }, () => {
      // Track modal closing event
      this.trackChatModalClosed();
    });
  };

  trackChatModalClosed = () => {
    // Send modal closed event to GTM dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'chat_modal_closed',
        event_category: 'Chat Interaction',
        event_action: 'Modal Closed',
        event_label: 'Chat Initialization Modal',
        current_language: window.current_language || 'pt',
        modal_type: 'chat_init_modal',
        component_context: 'chatbot'
      });
    }
  };

  handleInitializeChat = async ({ email, recaptchaToken }) => {
    try {
      // Store user email for potential future use
      this.setState({ 
        userEmail: email,
        chatInitialized: true 
      }, () => {
        // Reconfigure the chat with the user email after state is updated
        if (this.dtaChatApi) {
          // First, reconfigure the entire system with the new user ID
          this.dtaChatApi.config(this.getConfigWithUserId(email));
          
          // Then update headers
          this.dtaChatApi.addHeaders({
            'x-dta-project': 'dta-engenharia-chatbot',
            'x-dta-api-key': "sk-oO64YIhdhMtHZkm-uKtoM5pybu-yBqCk5F-rFJHLqPwaUGWW",
            'x-dta-user-id': email
          });
        }
      });
      
      // Close the speech bubble after successful initialization
      this.closeSpeechBubble();
      
      // Track successful chat initialization
      this.trackChatInitialized(email);
      
      // Here you could also send the email and recaptcha token to your backend
      // for verification and user registration if needed
      
      // Wait a bit for the state to update, then programmatically open the chat
      setTimeout(() => {
        this.openChatWidget();
      }, 100);
      
    } catch (error) {
      console.error('❌ Error in chat initialization:', error);
      throw error; // Re-throw to let the modal handle the error
    }
  };

  trackChatInitialized = (email) => {
    // Send chat initialized event to GTM dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'chat_initialized',
        event_category: 'Chat Interaction',
        event_action: 'Chat Initialized',
        event_label: 'User Email Provided',
        current_language: window.current_language || 'pt',
        user_email_domain: email ? email.split('@')[1] : 'unknown',
        component_context: 'chatbot',
        chat_type: 'protheus_release'
      });
    }
  };

  openChatWidget = () => {
    // First, try using the DTA API directly if available
    if (this.dtaChatApi) {
      // Try common method names for opening chat
      const openMethods = ['open', 'show', 'toggle', 'expand', 'activate'];
      for (const method of openMethods) {
        if (typeof this.dtaChatApi[method] === 'function') {
          try {
            this.dtaChatApi[method]();
            return; // Exit if successful
          } catch (error) {
            // Continue to next method
          }
        }
      }
    }
    
    // Fallback: simulate click on the chat anchor
    const chatAnchor = document.querySelector('dta-chat-anchor');
    if (chatAnchor) {
      // Remove our prevention handler temporarily
      if (this.preventDefaultHandler) {
        chatAnchor.removeEventListener('click', this.preventDefaultHandler, true);
      }
      
      // Create and dispatch multiple types of events
      const events = [
        new MouseEvent('mousedown', { bubbles: true, cancelable: true }),
        new MouseEvent('click', { bubbles: true, cancelable: true }),
        new MouseEvent('mouseup', { bubbles: true, cancelable: true })
      ];
      
      events.forEach(event => {
        chatAnchor.dispatchEvent(event);
      });
      
      // Try clicking elements in shadow DOM
      if (chatAnchor.shadowRoot) {
        const shadowElements = chatAnchor.shadowRoot.querySelectorAll('*');
        shadowElements.forEach(element => {
          if (element.click && (element.tagName === 'BUTTON' || element.role === 'button' || element.onclick)) {
            try {
              element.click();
            } catch (error) {
              // Continue to next element
            }
          }
        });
      }
      
      // Re-add our prevention handler
      if (this.preventDefaultHandler) {
        chatAnchor.addEventListener('click', this.preventDefaultHandler, true);
      }
    }
    
    // Additional fallback: look for any DTA-related global functions
    setTimeout(() => {
      if (window.dtaChat) {
        const openMethods = ['open', 'show', 'toggle', 'expand', 'activate'];
        for (const method of openMethods) {
          if (typeof window.dtaChat[method] === 'function') {
            try {
              window.dtaChat[method]();
              return;
            } catch (error) {
              // Continue to next method
            }
          }
        }
      }
      
      // Last resort: try to find and click any visible chat elements
      const allChatElements = document.querySelectorAll('dta-chat, dta-chat-anchor, [class*="dta"], [id*="dta"]');
      
      allChatElements.forEach((element, index) => {
        if (element.click) {
          try {
            element.click();
          } catch (error) {
            // Continue to next element
          }
        }
      });
    }, 300);
  };

  handleDtaChatReady = (event) => {
    try {
      const dtaChatApi = event.detail;
      
      // Store the API for later initialization after user verification
      this.dtaChatApi = dtaChatApi;

      const apiKey = "sk-WS6aP34pCeqUFSA-XTfF5AfRSO-JenRQuC-Yu7iB0j6emr0q";
      const userId = this.state.userEmail || `anonymous_${Date.now()}`;

      // Configure the chatbot with current user info
      dtaChatApi.config(this.getConfigWithUserId(userId));
      dtaChatApi.addHeaders({
        'x-dta-project': 'dta-engenharia-chatbot',
        'x-dta-api-key': apiKey,
        'x-dta-user-id': userId
      });
      dtaChatApi.addContext({
        frontendVersion: this.state.frontendVersion,
        currentLanguage: this.state.currentLanguage,
        top_k: 5,
      });

      this.setState({ chatbotReady: true });
      
    } catch (error) {
      console.error('❌ Error configuring DTA Chat:', error);
      this.setState({ error: error.message });
    }
  };

  getConfig = () => {
    const userId = this.state.userEmail || `anonymous_${Date.now()}`;
    return this.getConfigWithUserId(userId);
  };

  getConfigWithUserId = (userId) => {
    const baseUrl = 'https://totvs.dta.totvs.ai/api/flows/workflow/068cadf1-a069-72be-8000-81fba0f992f9/versions/38';

    return {
      apiInit: {
        url: baseUrl + '/run',
        sendHeaders: ['x-dta-project', 'x-dta-api-key', 'x-dta-user-id'],
      },
      apiMessage: {
        url: baseUrl + '/run',
        method: 'POST',
        sendHeaders: ['x-dta-project', 'x-dta-api-key', 'x-dta-user-id'],
      },
      header_session_prefix: null,
      header_thread_prefix: null,
      header_session: 'x-dta-session-id',
      header_thread: 'x-dta-trace-id',
      user_id: userId, // Explicitly set user ID in config
    };
  };

  closeSpeechBubble = () => {
    this.setState({ showSpeechBubble: false });
  };

  setupLinkObserver = () => {
    // Wait for dta-chat element to be available
    const waitForDtaChat = () => {
      const dtaChatElement = document.querySelector('dta-chat');
      
      if (dtaChatElement && dtaChatElement.shadowRoot) {
        this.startObservingLinks(dtaChatElement.shadowRoot);
        
        // Also set up periodic check as additional safety net
        this.setupPeriodicLinkCheck();
      } else if (dtaChatElement) {
        setTimeout(waitForDtaChat, 500);
      } else {
        setTimeout(waitForDtaChat, 500);
      }
    };
    
    waitForDtaChat();
  };

  startObservingLinks = (targetElement) => {
    // First, process any existing links
    this.processExistingLinks(targetElement);
    
    // Create MutationObserver to watch for new content
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Check for added nodes
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node is a link
              if (node.tagName === 'A') {
                this.processLink(node);
              }
              
              // Check for links within the added node
              const links = node.querySelectorAll ? node.querySelectorAll('a') : [];
              if (links.length > 0) {
                links.forEach(link => this.processLink(link));
              }
            }
          });
        }
        
        // Check for attribute changes on existing links
        if (mutation.type === 'attributes' && mutation.target.tagName === 'A') {
          this.processLink(mutation.target);
        }
      });
    });
    
    // Start observing with more comprehensive options
    observer.observe(targetElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeOldValue: true,
      characterData: true,
      characterDataOldValue: true
    });
    
    // Store the observer (create array if needed for multiple observers)
    if (!this.linkObserver) {
      this.linkObserver = [];
    }
    this.linkObserver.push(observer);
  };

  processExistingLinks = (container) => {
    const existingLinks = container.querySelectorAll('a');
    
    existingLinks.forEach((link, index) => {
      this.processLink(link);
    });
  };

  processLink = (linkElement) => {
    if (!linkElement || linkElement.tagName !== 'A') {
      return;
    }
    
    // Check if link already has the required attributes
    const currentTarget = linkElement.getAttribute('target');
    const currentRel = linkElement.getAttribute('rel');
    
    if (!currentTarget || currentTarget !== '_blank') {
      linkElement.setAttribute('target', '_blank');
    }
    
    if (!currentRel || !currentRel.includes('noopener noreferrer')) {
      linkElement.setAttribute('rel', 'noopener noreferrer');
    }
  };

  disconnectLinkObserver = () => {
    if (this.linkObserver) {
      if (Array.isArray(this.linkObserver)) {
        this.linkObserver.forEach((observer, index) => {
          observer.disconnect();
        });
      } else {
        this.linkObserver.disconnect();
      }
      this.linkObserver = null;
    }
    
    // Clear periodic check
    if (this.periodicLinkCheck) {
      clearInterval(this.periodicLinkCheck);
      this.periodicLinkCheck = null;
    }
  };

  setupPeriodicLinkCheck = () => {
    // Check for new links every 2 seconds as a fallback
    this.periodicLinkCheck = setInterval(() => {
      // Check both regular DOM and shadow DOM
      const dtaChatElement = document.querySelector('dta-chat');
      let allLinks = [];
      
      // Get links from shadow DOM
      if (dtaChatElement && dtaChatElement.shadowRoot) {
        const shadowLinks = dtaChatElement.shadowRoot.querySelectorAll('a[href]');
        allLinks = [...allLinks, ...shadowLinks];
      }
      
      // Get links from regular DOM as fallback
      const regularLinks = document.querySelectorAll('dta-chat a, a[href]');
      allLinks = [...allLinks, ...regularLinks];
      
      if (allLinks.length > 0) {
        allLinks.forEach(link => {
          const hasCorrectTarget = link.getAttribute('target') === '_blank';
          const hasCorrectRel = link.getAttribute('rel') === 'noopener noreferrer';
          
          if (!hasCorrectTarget || !hasCorrectRel) {
            this.processLink(link);
          }
        });
      }
    }, 2000);
  };

  render() {
    const { showSpeechBubble, showInitModal, currentLanguage } = this.state;

    return (
      <div className="chatbot-container">
        {/* Speech Bubble */}
        {showSpeechBubble && (
          <div className="speech-bubble">
            <div className="speech-bubble-content">
              <span className="speech-bubble-text">{t('chatbot_message', currentLanguage)}</span>
              {/* <button 
                className="speech-bubble-close" 
                onClick={this.closeSpeechBubble}
                aria-label="Close speech bubble"
              >
                ×
              </button> */}
            </div>
            <div className="speech-bubble-arrow"></div>
          </div>
        )}

        {/* Chat Initialization Modal */}
        <ChatInitModal
          isOpen={showInitModal}
          onClose={this.handleCloseInitModal}
          onInitializeChat={this.handleInitializeChat}
        />

        {/* DTA Chat Elements */}
        <dta-chat 
          id="protheus-chatbot" 
          label="Assistente Protheus Release"
          data-gtm-category="chat"
          data-gtm-action="chat-widget"
          data-gtm-label="protheus-assistant"
        ></dta-chat>
        <dta-chat-anchor 
          id="protheus-chat-anchor" 
          size="44"
          data-gtm-category="chat"
          data-gtm-action="chat-anchor-click"
          data-gtm-label="open-chat-modal"
          data-chat-type="protheus-release"
          role="button"
          aria-label="Abrir chat do assistente Protheus"
        ></dta-chat-anchor>
      </div>
    );
  }
}

export default Chatbot2510;
